import type { CommunityTemplateStatus } from '../../types/api';

/**
 * What a template's status is called on screen, what it means, and whether the
 * author may still change it.
 *
 * The vocabulary lives here rather than in each screen: the studio and the
 * member's own list say the same words about the same row, and the editable
 * rule is the backend's own (`draft` and `rejected`, anything else 409
 * `REQUEST_NOT_EDITABLE`) written once.
 */

export const COMMUNITY_STATUS_LABEL: Readonly<Record<CommunityTemplateStatus, string>> = {
  draft: 'Draft',
  pending: 'In review',
  approved: 'Approved',
  rejected: 'Needs changes',
};

/**
 * One line saying what the status means for the author, in their terms. Every
 * status is usable by its own business straight away; the status is only about
 * sharing it with everyone else.
 */
export const COMMUNITY_STATUS_BLURB: Readonly<Record<CommunityTemplateStatus, string>> = {
  draft:
    'Only you can see it, and you can post on it now. Submit it to share it with every member.',
  pending:
    'You can post on it now. The review team has it for the gallery, so it cannot be edited until they answer.',
  approved: 'In the community gallery. You and every other member can post on it.',
  rejected:
    'Not shared: sent back with a note. You can still post on it; fix what the note names to submit again.',
};

/** Chip tone, matching the status chips the history log already uses. */
export function communityStatusTone(status: CommunityTemplateStatus): string {
  switch (status) {
    case 'pending':
      return 'status-chip status-chip--waiting';
    case 'approved':
      return 'status-chip status-chip--cleared';
    case 'rejected':
      return 'status-chip status-chip--failed';
    case 'draft':
      return 'status-chip';
  }
}

/** The backend's own rule: a template is the author's only before review. */
export function isCommunityTemplateEditable(status: CommunityTemplateStatus): boolean {
  return status === 'draft' || status === 'rejected';
}
